import { BaseSyntheticEvent } from "react";

export function getEmailValidationPattern() {
  return /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
}

export const checkEmail = (event: BaseSyntheticEvent) => {
  const email = event.target.value;

  return getEmailValidationPattern().test(email);
};

export function getPhoneValidationPattern() {
  return /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
}

export function toUsE164(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  return value.trim();
}

export function isUsE164(value: string): boolean {
  return /^\+1\d{10}$/.test(value);
}

export function getPasswordValidationPattern() {
  /*
        at least 1 lower case letter
        at least 1 capital letter
        at least 1 number
        at least 1 special character
        at least 8 characters
     */
  return /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8}$/;
}

export function passwordHasAnUpperCaseCharacter(password: string) {
  return password.match(/^(?=.*[A-Z]).*$/);
}

export function passwordHasALowerCaseCharacter(password: string) {
  return password.match(/^(?=.*[a-z]).*$/);
}

export function passwordHasANumber(password: string) {
  return password.match(/^(?=.*[0-9]).*$/);
}

export function passwordHasASpecialCharacter(password: string) {
  return password.match(/^(?=.*\W)(?!.* ).*$/);
}

export function passwordHasAtLeastNCharacters(password: string, n: number) {
  const regex = new RegExp(`^(?!.* ).{${n},}$`);
  return password.match(regex);
}

export const PASSWORD_MIN_LENGTH = 12;

export const PASSWORD_REQUIREMENTS_HINT =
  "At least 12 characters with an uppercase letter, a lowercase letter, a number, and a special character.";

interface PasswordValidationEntry {
  rule: RegExp;
  message: string;
}

interface ValidatePasswordParams {
  minChars: Number;
}

export function hookFormValidatePassword(params: ValidatePasswordParams) {
  const { minChars } = params;

  const rules: Array<PasswordValidationEntry> = [
    {
      rule: /^(?=.*[A-Z]).*$/,
      message: "Should contain at least an uppercase letter",
    },
    {
      rule: /^(?=.*[a-z]).*$/,
      message: "Should contain at least a lowercase letter",
    },
    { rule: /^(?=.*[0-9]).*$/, message: "Should contain at least a number" },
    {
      rule: /^(?=.*\W)(?!.* ).*$/,
      message: "Should contain at least a special character",
    },
    {
      rule: new RegExp(`^(?!.* ).{${minChars},}$`),
      message: `Should contain at least ${minChars} characters`,
    },
  ];

  return rules.reduce((acc, { rule, message }, idx) => {
    return { ...acc, [idx]: (value: string) => rule.test(value) || message };
  }, {});
}
